import React, { MutableRefObject, useRef } from 'react';
import { Box } from '@mui/system';
import throttle from 'lodash/throttle';
import { CustomIcon } from 'components/CustomIcon';
import { Typography } from 'components/Typography';
import palette from 'theme/palette';
import * as Styled from '../SearchAutocompleteResults.styles';
import {
  ResultItemProps,
  ImageVariant,
  AutocompleteItemType,
} from '../SearchAutocompleteResults.types';
import { Images } from './Images';

export const RowItem: React.FC<ResultItemProps> = ({
  item,
  imgVariant,
  query = '',
  shouldShowArrows,
  shoudShowImage = true,
  customIcon,
  index = 0,
  type = AutocompleteItemType.SUGGESTION,
  isSelected = false,
  isPreviousItemSelected = false,
  isNextItemSelected = false,
  shouldShowDividers = true,
  shouldShowIcon = false,
  shouldShowClearButton = false,
  invertQueryHighlighting = false,
  onClick,
  onClear,
  onItemHover,
}) => {
  const isCircle = imgVariant === ImageVariant.CIRCLE;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleItemClick = () => {
    onClick?.(item.value, type);
  };

  const handleClearClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear?.(item.value, type);
  };

  const shouldShowPlaceholder = !item.image && shoudShowImage;
  const showIcon = (shouldShowIcon || !!customIcon) && !shoudShowImage;

  const handleMouseEnter = throttle(() => {
    onItemHover?.(item.value ?? item.title ?? '', index, item);
    if (containerRef.current === null || isSelected || !shouldShowDividers) {
      return;
    }

    const dividers = getAdjacentDividers(
      containerRef,
      isPreviousItemSelected,
      isNextItemSelected,
    );
    dividers.forEach((divider) => {
      if (divider !== null && divider instanceof HTMLElement) {
        divider.style.opacity = '0';
      }
    });
  }, 300);

  const hanldeMouseLeave = () => {
    if (containerRef.current === null || isSelected || !shouldShowDividers) {
      return;
    }
    const dividers = getAdjacentDividers(
      containerRef,
      isPreviousItemSelected,
      isNextItemSelected,
    );

    dividers.forEach((divider) => {
      if (divider !== null && divider instanceof HTMLElement) {
        divider.style.opacity = '1';
      }
    });
  };

  const validatedLabel = (item.value ?? item.title)?.replace(
    /[^0-9a-zA-Z]+/g,
    '_',
  );
  const id = `autocomplete_${validatedLabel}_item_${type}_${
    index + 1
  }`.toLowerCase();
  const isTrending = item.type === 'trending';
  const iconName = isTrending ? 'trending' : 'search';
  const dataSelector = `search-${
    type === AutocompleteItemType.HISTORY ? 'history' : 'suggestion'
  }-drawer-link`;

  return (
    <Styled.RowContainer
      id={id}
      data-selector={dataSelector}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={hanldeMouseLeave}
      ref={containerRef}
      onClick={handleItemClick}
      isSelected={isSelected}
      withHover
    >
      {showIcon && (
        <Styled.IconContainer>
          {customIcon ?? <CustomIcon name={iconName} dense />}
        </Styled.IconContainer>
      )}
      {typeof item.image === 'string' && shoudShowImage && (
        <Styled.ImageContainer overflow="hidden" isCircle={isCircle}>
          <img src={item.image} width="100%" height="100%" alt={item.title} />
        </Styled.ImageContainer>
      )}
      {shouldShowPlaceholder && <Styled.ImageContainer />}
      {Array.isArray(item.image) && shoudShowImage && (
        <Images images={item.image} />
      )}
      <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
        {item.title && (
          <Typography variant="body" color={palette.grey['A400']}>
            {getHighlightedText(item.title, query, {
              inverse: invertQueryHighlighting,
            })}
          </Typography>
        )}
        <Typography variant="body">
          {getHighlightedText(item.value, query, {
            inverse: invertQueryHighlighting,
          })}
        </Typography>
        {!!item.subtitle && (
          <Typography variant="tiny" color={palette.grey['A400']}>
            {item.subtitle}
          </Typography>
        )}
      </Box>
      {shouldShowClearButton && (
        <Styled.RowClearButton
          iconName="close"
          dense
          onClick={handleClearClick}
        />
      )}
      {shouldShowArrows && (
        <Styled.ArrowWrapper onClick={handleItemClick}>
          <CustomIcon name="arrow-right" dense />
        </Styled.ArrowWrapper>
      )}
    </Styled.RowContainer>
  );
};

////

const letters = Array.from({ length: 26 }, (_, index) =>
  String.fromCharCode(97 + index),
);
const numbers = Array.from({ length: 10 }, (_, index) =>
  String.fromCharCode(48 + index),
);
const uppercaseLetters = Array.from({ length: 26 }, (_, index) =>
  String.fromCharCode(65 + index),
);
const ALLOWED_CHARACTERS = letters.concat(uppercaseLetters, numbers, [' ']);

const getAdjacentDividers = (
  ref: MutableRefObject<HTMLDivElement | null>,
  isPreviousItemSelected: boolean,
  isNextItemSelected: boolean,
) => {
  const nextDivider = isNextItemSelected ? null : ref.current?.nextSibling;
  const prevDivider = isPreviousItemSelected
    ? null
    : ref.current?.previousSibling;
  return [prevDivider, nextDivider];
};

const getHighlightedText = (
  text: string,
  highlight: string,
  options: {
    inverse?: boolean;
  } = {},
) => {
  const { inverse = false } = options;

  if (!highlight) {
    return text;
  }

  const splittedHighlight = highlight.split('');
  const filteredHighlight = splittedHighlight.filter((character) =>
    ALLOWED_CHARACTERS.includes(character),
  );
  const validHighlight = filteredHighlight.join('');
  const parts = text.split(new RegExp(`(${validHighlight})`, 'gi'));

  if (parts.length <= 1) {
    return text;
  }

  return (
    <span>
      {parts.map((part: string, index: number) => {
        const isHighlighted = inverse
          ? part.toLowerCase() !== highlight.toLowerCase()
          : part.toLowerCase() === highlight.toLowerCase();

        return (
          <span
            key={index}
            style={
              isHighlighted
                ? { fontWeight: '600', color: palette.primary.main }
                : {}
            }
          >
            {part}
          </span>
        );
      })}
    </span>
  );
};
